import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { GoldButton } from "../components/Buttons";
import Seo from "../components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls =
    "w-full h-12 bg-white/5 border border-white/15 rounded-lg px-4 text-white placeholder-white/35 focus:outline-none focus:border-[#C8A45D] transition-colors duration-200";

export default function Login() {
    const { user, login, register } = useAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState("signin");
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(false);
    const [showForgot, setShowForgot] = useState(false);

    if (user) return <Navigate to="/dashboard" replace />;

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setNotice("");
        setBusy(true);
        try {
            if (mode === "signin") await login(form.email, form.password);
            else await register(form.name, form.email, form.password);
            navigate("/dashboard");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    const forgot = async (e) => {
        e.preventDefault();
        setError("");
        setNotice("");
        setBusy(true);
        try {
            await axios.post(`${API}/api/auth/forgot-password`, { email: form.email }, { withCredentials: true });
            setNotice("If an account exists for that email, a reset link is on its way.");
            setShowForgot(false);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="min-h-[80vh] bg-[#111318] flex items-center justify-center px-5 sm:px-8 py-32">
            <Seo title="Sign In | Freedom Tax Filer" description="Sign in to your Freedom Tax Filer account or create one to follow your inquiries." path="/login" noindex />
            <div className="w-full max-w-md">
                <div className="text-center">
                    <p className="text-[#C8A45D] text-[12px] uppercase tracking-[0.2em]">Freedom Tax Filer</p>
                    <h1 className="font-display text-white text-3xl sm:text-4xl mt-4">
                        {mode === "signin" ? (
                            <>Welcome <em className="italic text-gold-gradient">back.</em></>
                        ) : (
                            <>Create your <em className="italic text-gold-gradient">account.</em></>
                        )}
                    </h1>
                </div>

                <div className="mt-8 flex border border-white/10 rounded-lg overflow-hidden" data-testid="auth-tabs">
                    <button
                        type="button"
                        data-testid="auth-tab-signin"
                        onClick={() => { setMode("signin"); setError(""); setShowForgot(false); }}
                        className={`flex-1 h-11 text-[12px] uppercase tracking-[0.14em] font-medium transition-colors duration-200 ${mode === "signin" ? "bg-[#C8A45D] text-[#111318]" : "text-white/60 hover:text-white"}`}
                    >
                        Sign In
                    </button>
                    <button
                        type="button"
                        data-testid="auth-tab-register"
                        onClick={() => { setMode("register"); setError(""); setShowForgot(false); }}
                        className={`flex-1 h-11 text-[12px] uppercase tracking-[0.14em] font-medium transition-colors duration-200 ${mode === "register" ? "bg-[#C8A45D] text-[#111318]" : "text-white/60 hover:text-white"}`}
                    >
                        Create Account
                    </button>
                </div>

                <form onSubmit={submit} className="mt-6 space-y-4" data-testid={mode === "signin" ? "login-form" : "register-form"}>
                    {mode === "register" && (
                        <div>
                            <label htmlFor="auth-name" className="block text-[11px] uppercase tracking-[0.14em] text-white/50 mb-2">Full name</label>
                            <input id="auth-name" required maxLength={120} autoComplete="name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className={inputCls} data-testid="auth-name" />
                        </div>
                    )}
                    <div>
                        <label htmlFor="auth-email" className="block text-[11px] uppercase tracking-[0.14em] text-white/50 mb-2">Email address</label>
                        <input id="auth-email" type="email" required autoComplete="email" autoCapitalize="none" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="auth-email" />
                    </div>
                    <div>
                        <label htmlFor="auth-password" className="block text-[11px] uppercase tracking-[0.14em] text-white/50 mb-2">Password</label>
                        <input id="auth-password" type="password" required minLength={8} autoComplete={mode === "signin" ? "current-password" : "new-password"} value={form.password} onChange={set("password")} placeholder={mode === "register" ? "At least 8 characters" : "Your password"} className={inputCls} data-testid="auth-password" />
                    </div>

                    {error && <p className="text-[#e07a5f] text-sm" data-testid="auth-error">{error}</p>}
                    {notice && <p className="text-[#8fb996] text-sm" data-testid="auth-notice">{notice}</p>}

                    <GoldButton type="submit" disabled={busy} className="w-full h-12" data-testid="auth-submit">
                        {busy ? "Please wait…" : mode === "signin" ? "Sign In" : "Create Account"}
                    </GoldButton>
                </form>

                <div className="mt-5 text-center">
                    {mode === "signin" && !showForgot && (
                        <button type="button" className="text-white/50 text-sm hover:text-[#C8A45D] transition-colors duration-200" data-testid="forgot-password-link" onClick={() => setShowForgot(true)}>
                            Forgot your password?
                        </button>
                    )}
                    {showForgot && (
                        <form onSubmit={forgot} className="mt-2 space-y-3" data-testid="forgot-form">
                            <input type="email" required autoComplete="email" autoCapitalize="none" value={form.email} onChange={set("email")} placeholder="Your account email" className={inputCls} data-testid="forgot-email" />
                            <button type="submit" disabled={busy} className="text-[#C8A45D] text-sm underline underline-offset-4" data-testid="forgot-submit">
                                Send reset link
                            </button>
                        </form>
                    )}
                </div>

                <p className="text-white/40 text-xs text-center mt-8">
                    By continuing you agree to our{" "}
                    <Link to="/terms" className="text-white/60 hover:text-[#C8A45D] transition-colors duration-200">Terms</Link> and{" "}
                    <Link to="/privacy" className="text-white/60 hover:text-[#C8A45D] transition-colors duration-200">Privacy Policy</Link>.
                </p>
            </div>
        </section>
    );
}
